import React, { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faInfoCircle,
  faShieldAlt,
  faExclamationTriangle,
  faCheckCircle,
  faSearch,
  faEnvelope,
  faPaperclip,
  faFileAlt,
  faFilePdf,
  faFileImage,
  faFileArchive,
  faFileCode,
} from "@fortawesome/free-solid-svg-icons";
import {
  getConfidenceDescription,
  getConfidencePercentage,
} from "../utils/confidenceUtils";
import {
  decodeQuotedPrintable,
  formatDate,
  decodeAndSanitizeHtml,
} from "../utils/emailAnalysisUtils";
import EmailAnalysisAPI from "../../../services/EmailAnalysisAPI";
import SuggestionsBulletList from "../../common/SuggestionsBulletList";
import ThreatBannerShareButton from "../../common/ThreatBannerShareButton";

const AnalysisDetails = ({ selectedAnalysis, onScrollToAnalysisResults }) => {
  const [originalEmail, setOriginalEmail] = useState(null);
  const [showOriginalEmailModal, setShowOriginalEmailModal] = useState(false);
  const [modalEmailContent, setModalEmailContent] = useState("");

  // Utility functions
  const decodeEmailContent = (content) => {
    if (!content) return "";

    try {
      let decoded = content;

      // Handle Quoted-Printable encoding
      if (content.includes("=")) {
        try {
          decoded = content
            .replace(/=\r?\n/g, "")
            .replace(/=([0-9A-F]{2})/gi, (match, hex) => {
              return String.fromCharCode(parseInt(hex, 16));
            });
        } catch (e) {
          console.warn("Error decoding quoted-printable:", e);
        }
      }

      // Handle URL encoding
      if (decoded.includes("%")) {
        try {
          decoded = decodeURIComponent(decoded);
        } catch (e) {
          try {
            decoded = decodeURIComponent(escape(decoded));
          } catch (e2) {
            console.warn("Error with URL decoding:", e2);
          }
        }
      }

      // Clean up email content
      decoded = decoded
        .replace(
          /^(From|To|Subject|Date|Message-ID|Return-Path|Received|MIME-Version|Content-Type|Content-Transfer-Encoding|X-[\w-]+):.*$/gim,
          "",
        )
        .replace(/--[\w-]+/g, "")
        .replace(/Content-Type:.*$/gim, "")
        .replace(/Content-Transfer-Encoding:.*$/gim, "")
        .replace(/charset=[\w-]+/gi, "")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ")
        .replace(/[\u200B-\u200D\u2060\uFEFF]/g, "")
        .replace(/[\u034F]/g, "")
        .replace(/[\u061C]/g, "")
        .replace(/[\u180E]/g, "")
        .replace(/\n\s*\n\s*\n/g, "\n\n")
        .replace(/[ \t]+/g, " ")
        .replace(/^\s+|\s+$/g, "");

      if (!/<[^>]+>/.test(decoded)) {
        decoded = decoded
          .replace(/\n/g, "<br>")
          .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;");
      }

      return decoded;
    } catch (error) {
      console.warn("Error decoding email content:", error);
      return content || "";
    }
  };

  const displayEmailInModal = (emailContent) => {
    const iframe = document.getElementById("email-frame");
    if (!iframe) return;

    const decodedContent = decodeEmailContent(emailContent);

    const styledHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1">
        <style>
          body {
            margin: 0;
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
            line-height: 1.5;
            color: #333;
            background: white;
            font-size: 14px;
          }
          * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            margin: 8px 0;
          }
          td {
            padding: 8px 12px;
            vertical-align: top;
          }
          img {
            max-width: 100%;
            height: auto;
          }
          p {
            margin: 12px 0;
          }
          a {
            color: #007bff;
            text-decoration: none;
          }
          a:hover {
            text-decoration: underline;
          }
          @media (max-width: 600px) {
            body {
              padding: 12px;
              font-size: 13px;
            }
            table {
              font-size: 13px;
            }
          }
        </style>
      </head>
      <body>
        ${decodedContent}
      </body>
      </html>
    `;

    iframe.srcdoc = styledHTML;
  };

  // Event handlers
  const handleShowOriginalEmail = async (analysisItem) => {
    try {
      //   const fullAnalysis = await EmailAnalysisAPI.getAnalysisResult(
      //     analysisItem.key
      //   );
      setModalEmailContent(
        analysisItem?.originalEmailContent || "No email content available",
      );
      setShowOriginalEmailModal(true);
    } catch (err) {
      console.error(
        "AnalysisDetails: Error loading original email for modal:",
        err,
      );
      setModalEmailContent("Failed to load original email content.");
      setShowOriginalEmailModal(true);
    }
  };

  const closeOriginalEmailModal = () => {
    setShowOriginalEmailModal(false);
    setModalEmailContent("");
  };

  // Effects
  useEffect(() => {
    if (selectedAnalysis) {
      setOriginalEmail(selectedAnalysis.analysis?.originalEmailContent);
    }
  }, [selectedAnalysis]);

  useEffect(() => {
    if (showOriginalEmailModal) {
      document.body.classList.add("modal-open");

      const handleEscapeKey = (event) => {
        if (event.key === "Escape") {
          closeOriginalEmailModal();
        }
      };

      document.addEventListener("keydown", handleEscapeKey);

      return () => {
        document.body.classList.remove("modal-open");
        document.removeEventListener("keydown", handleEscapeKey);
      };
    } else {
      document.body.classList.remove("modal-open");
    }
  }, [showOriginalEmailModal]);

  useEffect(() => {
    if (showOriginalEmailModal && modalEmailContent) {
      const timer = setTimeout(() => {
        displayEmailInModal(modalEmailContent);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [showOriginalEmailModal, modalEmailContent]);

  if (!selectedAnalysis) {
    return null;
  }

  return (
    <>
      <div className="common-section analysis-details-section">
        <div className="common-section-header">
          <div className="section-header-content">
            <div className="section-header-left">
              <h3>
                <FontAwesomeIcon icon={faInfoCircle} /> Analysis Details
              </h3>
              <p>Detailed analysis results and original email content</p>
            </div>
            <div className="section-header-right">
              <button
                onClick={onScrollToAnalysisResults}
                className="common-button"
                title="Scroll to Analysis Results"
              >
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  className="fa-icon-margin-right-6"
                />
                Back to List
              </button>
            </div>
          </div>
        </div>
        <div className="common-section-content">
          <div className="analysis-results-container">
            <AnalysisSummary
              selectedAnalysis={selectedAnalysis}
              formatDate={formatDate}
              decodeQuotedPrintable={decodeQuotedPrintable}
            />

            <AnalysisSuggestions selectedAnalysis={selectedAnalysis} />

            <RiskFactors selectedAnalysis={selectedAnalysis} />

            <AnalysisReasoning selectedAnalysis={selectedAnalysis} />

            <AttachmentInfo selectedAnalysis={selectedAnalysis} />

            <EmailContent
              selectedAnalysis={selectedAnalysis}
              originalEmail={originalEmail}
              handleShowOriginalEmail={handleShowOriginalEmail}
              decodeEmailContent={decodeEmailContent}
            />
          </div>
        </div>
      </div>

      {/* Original Email Modal */}
      {showOriginalEmailModal && (
        <div className="email-modal-overlay" onClick={closeOriginalEmailModal}>
          <div
            className="email-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="email-modal-header">
              <h3>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="fa-icon-margin-right-8"
                />
                Original Email Content
              </h3>
              <button
                className="email-modal-close"
                onClick={closeOriginalEmailModal}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="email-modal-body">
              <iframe
                id="email-frame"
                title="Original Email Content"
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor: "#fff",
                }}
                sandbox="allow-same-origin"
              />
            </div>
            <div className="email-modal-footer">
              <button
                className="common-button"
                onClick={closeOriginalEmailModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const AnalysisSummary = ({
  selectedAnalysis,
  formatDate,
  decodeQuotedPrintable,
}) => {
  // Helper function to determine threat type display
  const getThreatTypeDisplay = (scamType, confidence, isScam) => {
    if (confidence <= 0.2) {
      return "None";
    }

    // If email is legitimate (not a scam), show "None identified"
    if (isScam === false) {
      return "None identified";
    }

    return scamType || "Unknown";
  };

  return (
    <div className="common-form-group">
      <h4>Analysis Summary</h4>
      {/* Threat Assessment Banner */}
      <div
        className={`threat-banner ${
          selectedAnalysis.analysis?.analysisResult?.isScam
            ? "threat-detected"
            : "safe"
        }`}
      >
        <div className="threat-icon">
          <FontAwesomeIcon
            icon={
              selectedAnalysis.analysis?.analysisResult?.isScam
                ? faExclamationTriangle
                : faCheckCircle
            }
          />
        </div>
        <div className="threat-content">
          <h2 className="threat-title">
            {selectedAnalysis.analysis?.analysisResult?.isScam
              ? "SCAM DETECTED"
              : "NO THREATS DETECTED"}
          </h2>
          <p className="threat-subtitle">
            {selectedAnalysis.analysis?.analysisResult?.isScam
              ? "This email contains suspicious or fraudulent content"
              : "This email appears to be legitimate"}
          </p>
        </div>
        <ThreatBannerShareButton
          scamDetected={
            selectedAnalysis.analysis?.analysisResult?.isScam === true
          }
          threatType="Email Scam"
          threatTitle="Email Scam Detected"
          threatDetail="This email contains suspicious or fraudulent content"
        />
      </div>
    </div>
  );
};

const AnalysisSuggestions = ({ selectedAnalysis }) => {
  // Primary suggestions field from backend
  let suggestions = selectedAnalysis?.analysis?.analysisResult?.suggestions;

  // Fallback: try to extract recommendations from the reasoning/explanation text
  const explanation =
    selectedAnalysis?.analysis?.analysisResult?.reasoning ||
    selectedAnalysis?.analysis?.analysisResult?.explanation ||
    "";

  const extractRecommendations = (explanationText, fallback) => {
    if (!explanationText) return fallback || null;

    const patterns = [
      /###\s*(?:Safety\s+)?Recommendations?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /###\s*Suggestions?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /\*\*(?:Safety\s+)?Recommendations?\*\*\s*\n([\s\S]*?)(?=\n\*\*|\n\n\*\*|$)/i,
    ];

    for (const pattern of patterns) {
      const match = explanationText.match(pattern);
      if (match && match[1]) {
        return match[1].trim().replace(/\n{3,}/g, "\n\n");
      }
    }

    return fallback || null;
  };

  if (!suggestions) {
    suggestions = extractRecommendations(explanation, null);
  }

  if (!suggestions) return null;

  return (
    <div className="suggestions-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon
          icon={faInfoCircle}
          className="fa-icon-margin-right-8"
        />
        What Should You Do?
      </h4>
      <SuggestionsBulletList text={suggestions} />
    </div>
  );
};

const RiskFactors = ({ selectedAnalysis }) => {
  const suspiciousElements =
    selectedAnalysis.analysis?.analysisResult?.suspiciousElements;

  if (!suspiciousElements || suspiciousElements.length === 0) {
    return null;
  }

  return (
    <div className="risk-factors-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="fa-icon-margin-right-8"
        />
        Risk Factors ({suspiciousElements.length})
      </h4>
      <ul className="risk-factors-list">
        {suspiciousElements.map((factor, index) => (
          <li key={index}>
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="risk-icon"
            />
            {factor}
          </li>
        ))}
      </ul>
    </div>
  );
};

const AnalysisReasoning = ({ selectedAnalysis }) => {
  if (!selectedAnalysis.analysis?.analysisResult?.reasoning) {
    return null;
  }

  return (
    <div className="explanation-section">
      <h4 className="subsection-title">
        <FontAwesomeIcon icon={faSearch} className="fa-icon-margin-right-8" />
        Explanation
      </h4>
      <div
        className="explanation-text"
        dangerouslySetInnerHTML={{
          __html: decodeAndSanitizeHtml(
            selectedAnalysis.analysis.analysisResult.reasoning || "",
          ),
        }}
      />
    </div>
  );
};

const AttachmentInfo = ({ selectedAnalysis }) => {
  if (
    !selectedAnalysis?.analysisResult?.analyzedEmailContent?.attachmentNames ||
    selectedAnalysis.analysisResult.analyzedEmailContent.attachmentNames
      .length === 0
  ) {
    return null;
  }

  const attachments =
    selectedAnalysis.analysisResult.analyzedEmailContent.attachmentNames;

  // Helper function to get appropriate icon for file type
  const getFileIcon = (filename) => {
    const extension = filename.toLowerCase().split(".").pop();

    switch (extension) {
      case "pdf":
        return faFilePdf;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
        return faFileImage;
      case "zip":
      case "rar":
      case "7z":
      case "tar":
      case "gz":
        return faFileArchive;
      case "exe":
      case "bat":
      case "com":
      case "scr":
      case "cmd":
        return faFileCode;
      default:
        return faFileAlt;
    }
  };

  // Helper function to get risk level for file type
  const getAttachmentRiskLevel = (filename) => {
    const extension = filename.toLowerCase().split(".").pop();
    const highRiskExtensions = [
      "exe",
      "scr",
      "bat",
      "com",
      "cmd",
      "pif",
      "jar",
    ];
    const mediumRiskExtensions = [
      "zip",
      "rar",
      "7z",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
    ];

    if (highRiskExtensions.includes(extension)) {
      return "high";
    } else if (mediumRiskExtensions.includes(extension)) {
      return "medium";
    }
    return "low";
  };

  return (
    <div className="common-form-group">
      <h4>
        <FontAwesomeIcon
          icon={faPaperclip}
          className="fa-icon-margin-right-8"
        />
        Email Attachments ({attachments.length})
      </h4>
      <div className="attachment-list">
        {attachments.map((attachment, index) => {
          const riskLevel = getAttachmentRiskLevel(attachment);
          const fileIcon = getFileIcon(attachment);

          return (
            <div
              key={index}
              className={`attachment-item attachment-risk-${riskLevel}`}
            >
              <div className="attachment-icon">
                <FontAwesomeIcon icon={fileIcon} />
              </div>
              <div className="attachment-details">
                <div className="attachment-name">{attachment}</div>
                <div className="attachment-risk">
                  {riskLevel === "high" && (
                    <span className="risk-high">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      High Risk File Type
                    </span>
                  )}
                  {riskLevel === "medium" && (
                    <span className="risk-medium">
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      Medium Risk File Type
                    </span>
                  )}
                  {riskLevel === "low" && (
                    <span className="risk-low">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Generally Safe File Type
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="attachment-warning">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="fa-icon-margin-right-8"
        />
        <strong>Warning:</strong> Never open attachments from untrusted sources.
        Executable files (.exe, .bat, .com, .scr) are particularly dangerous and
        should be treated with extreme caution.
      </div>
    </div>
  );
};

const EmailContent = ({
  selectedAnalysis,
  originalEmail,
  handleShowOriginalEmail,
  decodeEmailContent,
}) => {
  return (
    <>
      <div className="common-form-group">
        <h4>
          <FontAwesomeIcon
            icon={faEnvelope}
            className="fa-icon-margin-right-8"
          />
          Original Email
        </h4>
        <div className="common-button-group">
          <button
            className="common-button"
            onClick={() => handleShowOriginalEmail(selectedAnalysis)}
          >
            <FontAwesomeIcon
              icon={faEnvelope}
              className="fa-icon-margin-right-8"
            />
            Show Email Content
          </button>
        </div>
      </div>

      {originalEmail && (
        <div className="common-form-group">
          <h4>
            <FontAwesomeIcon
              icon={faEnvelope}
              className="fa-icon-margin-right-8"
            />
            Original Email Content Preview
          </h4>
          <div className="analysis-explanation original-email-content">
            <div
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "system-ui, -apple-system, sans-serif",
                lineHeight: "1.5",
              }}
              dangerouslySetInnerHTML={{
                __html: (() => {
                  const content = originalEmail || "No content available";
                  const truncateLength = 2000;
                  const truncated =
                    content.length > truncateLength
                      ? content.substring(0, truncateLength) + "..."
                      : content;
                  return DOMPurify.sanitize(decodeEmailContent(truncated), {
                    ALLOWED_TAGS: [
                      "p",
                      "br",
                      "div",
                      "span",
                      "strong",
                      "em",
                      "u",
                      "a",
                      "table",
                      "tr",
                      "td",
                      "th",
                      "tbody",
                      "thead",
                    ],
                    ALLOWED_ATTR: ["class", "style", "href", "target", "rel"],
                  });
                })(),
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AnalysisDetails;
